import { useLocationQuery } from "@/offering/api";
import { useFMDownloadReceiptFeatureFlag } from "@/core/splitio";
import { Order } from "@/fm/data/orders";
import { membershipLocatableCountry } from "@/fm/utils/membership_locatable_country";

interface UseReceiptDownloadEnabledParams {
  partnerId: string;
  userId: string;
  // The order's space, when it has one. Its location must resolve before the
  // country is known, so the receipt is withheld until then.
  spaceId?: string;
  // Resolves the country the receipt belongs to. Absent for orders with no
  // space (memberships), which fall back to the membership's own locatable.
  locationId?: string;
  order?: Order;
}

/**
 * Whether a Flexspace receipt PDF is offered for this order at all.
 *
 * Availability is a property of the receipt, not of the screen showing it, so
 * every surface that renders a download control reads it from here. Scattering
 * the check leaves a suppressed receipt reachable from whichever surface
 * forgot to ask.
 *
 * Suppression is keyed on the location's country rather than the partner
 * because a partner can operate in several countries at once, and only some
 * jurisdictions need their receipts withheld.
 */
export function useReceiptDownloadEnabled({
  partnerId,
  userId,
  spaceId,
  locationId,
  order,
}: UseReceiptDownloadEnabledParams) {
  const { data: location } = useLocationQuery(locationId);
  const locationCountry =
    location?.country ?? membershipLocatableCountry(order);

  const allowedByFlag = useFMDownloadReceiptFeatureFlag({
    partnerId,
    userId,
    locationCountry,
  });

  // The country decides availability, so an unresolved location means we don't
  // yet know. A space whose location id has not arrived is the same unknown:
  // that first render sees no location id at all and would otherwise read as
  // "nothing to wait for". Withhold rather than offer: rendering the control
  // and then retracting it invites a click on the very receipt we mean to
  // suppress, and reflows the header around it.
  const countryPending =
    (Boolean(spaceId) && !locationId) || (Boolean(locationId) && !location);
  if (countryPending) return false;

  return allowedByFlag;
}
